import { ComponentProps, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'

import { WidgetButton } from '@/fragments/WidgetToolbar/WidgetButton'
import { Icon, IconButton } from '@/shared/components'
import { MosaicDragItem, MosaicDragType } from '@/shared/components/Mosaic'
import { isToolWidgetType } from '@/shared/components/Mosaic/lib/isToolWidgetType'
import { WidgetNode } from '@/shared/components/Mosaic/models/widgetType'
import { pxToRem } from '@/shared/styles/themes'
import { faClose, faSquarePen } from '@fortawesome/pro-light-svg-icons'
import { Divider, Grid2 as Grid, styled } from '@mui/material'

import { OrderSearchBar } from '../OrderSearchBar/OrderSearchBar'
import { useWidgetToolbarContext } from './WidgetToolbarContext'

export type WidgetToolbarProps = Omit<ComponentProps<typeof Grid>, 'ref' | 'container' | 'direction'> & {
  widgets: WidgetNode[]
  setWidgets: (widgetNodes: WidgetNode[]) => void
}

export const WidgetToolbar: React.FC<WidgetToolbarProps> = ({ widgets, setWidgets, ...props }) => {
  const [isDraggedOver, setIsDraggedOver] = useState(false)
  const { isWidgetSettingsOpen, setWidgetSettingsOpen } = useWidgetToolbarContext()
  const [draggedOverIndex, setDraggedOverIndex] = useState(-1)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const { moveWidgetAroundToolbar, moveWidgetToToolbar } = useWidgetToolbarContext()
  const toolbarWidgets = widgets.filter(widget => widget.location === 'toolbar')

  // Determine the index where the widget should be dropped
  const determineDropIndex = (dropX: number, widgetCount: number, widgetWidth: number) => {
    const initialOffset = 32
    const totalWidgetsWidth = widgetCount * widgetWidth

    if (dropX > totalWidgetsWidth) {
      return widgetCount
    } else if (dropX < initialOffset) {
      return 0
    }

    return Math.floor((dropX + (widgetWidth - initialOffset)) / widgetWidth)
  }

  // Show the right sidebar to add additional widgets to the toolbar if needed
  const onToggleWidgetSettings = () => {
    setWidgetSettingsOpen((prev) => !prev)
  }

  const [{ isOver }, dropRef] = useDrop({
    accept: [MosaicDragType.WINDOW, MosaicDragType.WIDGET_BUTTON],
    drop: (item: MosaicDragItem, monitor) => {
      const clientOffset = monitor.getClientOffset()

      // Compute drop index to insert the widget
      let droppedIndex = -1

      if (clientOffset && toolbarRef && toolbarRef.current) {
        const hoverTargetRect = toolbarRef.current.getBoundingClientRect()
        const hoverTargetX = clientOffset.x - hoverTargetRect.left

        droppedIndex = determineDropIndex(hoverTargetX, widgets.length, 56)
      }

      if (droppedIndex < 0) {
        return
      }

      if (item.type === MosaicDragType.WIDGET_BUTTON) {
        moveWidgetAroundToolbar(item.index, droppedIndex)
      } else if (item.type === MosaicDragType.WINDOW && isToolWidgetType(item.widgetType)) {
        // Dock the widget to the toolbar
        moveWidgetToToolbar(item.widgetType, droppedIndex)

        return { isDocked: true }
      }

      // TODO: Handle new widget insertion
    },
    hover: (item, monitor) => {
      if (!isToolWidgetType(item.widgetType)) return

      const clientOffset = monitor.getClientOffset()

      let hoveredIndex = -1

      if (clientOffset && toolbarRef && toolbarRef.current) {
        const hoverTargetRect = toolbarRef.current.getBoundingClientRect()
        const hoverTargetX = clientOffset.x - hoverTargetRect.left

        hoveredIndex = determineDropIndex(hoverTargetX, widgets.length, 56)
      }

      setDraggedOverIndex(hoveredIndex)
      setIsDraggedOver(monitor.isOver())
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
    }),
  })

  const onWidgetButtonClick = (widget: WidgetNode) => {
    //TODO: Handle widget button click by opening the widget window in a portal
    console.log('DEBUG onWidgetButtonClick: ', widget)
  }

  return (
    <StyledWidgetToolbar ref={dropRef}>
      <Grid container ref={toolbarRef} direction={'row'} justifyContent={'space-between'} spacing={2} sx={{ width: '100%' }} {...props}>
        <Grid sx={{ display: 'inline-flex' }}>
          {toolbarWidgets.map((widget, index) => (
            <StyledWidgetButtonContainer key={index}>
              {isOver && isDraggedOver && draggedOverIndex === index && <StyledPlaceholder />}
              <WidgetButton
                index={index}
                icon={widget.icon}
                tooltipTitle={widget.tooltip}
                widgetType={widget.widgetType}
                onClick={() => { onWidgetButtonClick(widget) }}
              />
            </StyledWidgetButtonContainer>
          ))}
          <StyledWidgetButtonContainer>
            {isOver && isDraggedOver && draggedOverIndex === toolbarWidgets.length && <StyledPlaceholder />}
          </StyledWidgetButtonContainer>
          <Divider orientation="vertical" flexItem />
          <IconButton
            tooltipTitle={isWidgetSettingsOpen ? 'Cancel' : 'Add/remove widgets'}
            sx={{ marginTop: 0, marginBottom: 0 }}
            onClick={onToggleWidgetSettings}
            slotProps={{
              tooltip: {
                placement: 'bottom',
              },
            }}
          >
            <Icon icon={isWidgetSettingsOpen ? faClose : faSquarePen} />
          </IconButton>
        </Grid>
        <Grid flexGrow={1}>
          <OrderSearchBar />
        </Grid>
      </Grid>
    </StyledWidgetToolbar>
  )
}

const StyledPlaceholder = styled('div')`
  height: ${pxToRem(28)};
  width: ${pxToRem(48)};
  background-color: ${({ theme }) => theme.palette.custom.widgetDropTarget.backgroundColor};
  border-radius: ${pxToRem(8)};
  border: ${({ theme }) => theme.palette.custom.widgetDropTarget.border};
`

const StyledWidgetButtonContainer = styled(Grid)`
  position: relative;
  display: flex;
  flex-direction: row;
  gap: ${pxToRem(8)};
`

const StyledWidgetToolbar = styled(Grid)`
  border-radius: ${pxToRem(8)};
  background-color: ${({ theme }) => theme.palette.custom.widgetToolbar.backgroundColor};
  box-shadow: ${({ theme }) => theme.palette.custom.widgetToolbar.boxShadow};
  width: calc(100% - ${pxToRem(20)});
  min-height: ${props => props.theme.layout.widgetToolbarHeight};
  padding: ${props => props.theme.typography.pxToRem(8)} ${props => props.theme.typography.pxToRem(8)};
  margin: 0 0 0 ${props => props.theme.typography.pxToRem(8)};
  display: flex;
  align-items: center;
`
